import { ReactNode, useState } from 'react'
import { Stack, styled, Typography, SxProps } from '@mui/material'
import { CopyIcon, DoneIcon } from '@/components/UI/Icons'

export const Copy = ({
  value,
  displayValue,
  textStyle,
  align = 'left',
}: {
  value: string
  // What is shown, when it differs from what is copied.
  displayValue?: ReactNode
  textStyle?: SxProps
  // Right-aligned, the value stays pinned to the right edge and the copy icon appears to its left.
  align?: 'left' | 'right'
}) => {
  const [copied, setCopied] = useState('')
  const [isHovered, setIsHovered] = useState(false)

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(value)
      setTimeout(() => setCopied(''), 1500)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  // The icon is always laid out and only shown on hover, so revealing it never widens the value
  // and a table around it keeps its column widths.
  return (
    <Stack
      gap={1}
      direction={align === 'right' ? 'row-reverse' : 'row'}
      alignItems="center"
      justifyContent={align === 'right' ? 'flex-start' : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Value onClick={() => handleCopy(value)} sx={textStyle}>
        {displayValue ?? value}
      </Value>

      {/* One footprint for both icons, so swapping to the tick after a copy does not shift anything. */}
      <IconSlot aria-hidden sx={{ visibility: isHovered ? 'visible' : 'hidden' }}>
        {copied !== value ? (
          <CopyIcon sx={{ padding: 0, fontSize: '18px' }} />
        ) : (
          <DoneIcon sx={{ color: '#667085', padding: 0, fontSize: '16px' }} />
        )}
      </IconSlot>
    </Stack>
  )
}

const IconSlot = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 18,
  height: 18,
})

export const Value = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 'normal',
  fontFamily: 'Inter',
  color: theme.palette.secondary.main,
  cursor: 'default',
}))
